import type { DieFace } from '../shared/types.js';
import { scoreSelection } from '../shared/scoring.js';
import { DEFAULT_TARGET_SCORE, DICE_COUNT, MAX_TARGET_SCORE, MIN_TARGET_SCORE } from '../shared/rules.js';

export type LocalPlayer = { name: string; score: number };

export type LocalSnapshot = {
  players: LocalPlayer[];
  activeIdx: number;
  phase: 'setup' | 'playing' | 'finished';
  turnPoints: number;
  draftDice: DieFace[];
  diceCommittedThisTurn: number;
  hotDiceFlag: boolean;
  winnerIdx: number | null;
  log: string[];
  label: string;
};

export type LocalGame = {
  phase: 'setup' | 'playing' | 'finished';
  players: LocalPlayer[];
  activeIdx: number;
  targetScore: number;
  turnPoints: number;
  draftDice: DieFace[];
  diceCommittedThisTurn: number;
  winnerIdx: number | null;
  log: string[];
  hotDiceFlag: boolean;
  prevSnapshot: LocalSnapshot | null;
};

const STORAGE_KEY = 'dice-local-v1';
const MAX_LOG = 30;

export function defaultLocalGame(): LocalGame {
  return {
    phase: 'setup',
    players: [{ name: '', score: 0 }, { name: '', score: 0 }],
    activeIdx: 0,
    targetScore: DEFAULT_TARGET_SCORE,
    turnPoints: 0,
    draftDice: [],
    diceCommittedThisTurn: 0,
    winnerIdx: null,
    log: [],
    hotDiceFlag: false,
    prevSnapshot: null,
  };
}

function takeSnapshot(g: LocalGame, label: string): LocalSnapshot {
  return {
    players: g.players.map(p => ({ ...p })),
    activeIdx: g.activeIdx,
    phase: g.phase,
    turnPoints: g.turnPoints,
    draftDice: [...g.draftDice],
    diceCommittedThisTurn: g.diceCommittedThisTurn,
    hotDiceFlag: g.hotDiceFlag,
    winnerIdx: g.winnerIdx,
    log: [...g.log],
    label,
  };
}

export function undoLastMove(g: LocalGame): boolean {
  const snap = g.prevSnapshot;
  if (!snap) return false;
  g.players = snap.players.map(p => ({ ...p }));
  g.activeIdx = snap.activeIdx;
  g.phase = snap.phase;
  g.turnPoints = snap.turnPoints;
  g.draftDice = [...snap.draftDice];
  g.diceCommittedThisTurn = snap.diceCommittedThisTurn;
  g.hotDiceFlag = snap.hotDiceFlag;
  g.winnerIdx = snap.winnerIdx;
  g.log = [...snap.log];
  g.prevSnapshot = null;
  pushLog(g, `Rückgängig: ${snap.label}.`);
  return true;
}

export function loadLocalGame(): LocalGame | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocalGame;
    return { ...defaultLocalGame(), ...parsed };
  } catch {
    return null;
  }
}

export function saveLocalGame(game: LocalGame | null): void {
  if (!game) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, JSON.stringify(game));
}

function pushLog(g: LocalGame, msg: string): void {
  g.log.push(msg);
  if (g.log.length > MAX_LOG) g.log.splice(0, g.log.length - MAX_LOG);
}

export function setPlayerName(g: LocalGame, idx: number, name: string): void {
  if (g.players[idx]) g.players[idx].name = name;
}

export function addPlayer(g: LocalGame): void {
  if (g.players.length >= 8) return;
  g.players.push({ name: '', score: 0 });
}

export function removePlayer(g: LocalGame, idx: number): void {
  if (g.players.length <= 2) return;
  g.players.splice(idx, 1);
}

export function movePlayerLocal(g: LocalGame, idx: number, direction: 'up' | 'down'): void {
  if (g.phase !== 'setup') return;
  const swap = direction === 'up' ? idx - 1 : idx + 1;
  if (swap < 0 || swap >= g.players.length) return;
  const tmp = g.players[idx];
  g.players[idx] = g.players[swap];
  g.players[swap] = tmp;
}

export function backToSetup(g: LocalGame): void {
  for (const p of g.players) p.score = 0;
  g.phase = 'setup';
  g.activeIdx = 0;
  g.turnPoints = 0;
  g.draftDice = [];
  g.diceCommittedThisTurn = 0;
  g.winnerIdx = null;
  g.hotDiceFlag = false;
  g.prevSnapshot = null;
}

export function setTargetScore(g: LocalGame, target: number): void {
  if (!Number.isFinite(target)) return;
  g.targetScore = Math.min(MAX_TARGET_SCORE, Math.max(MIN_TARGET_SCORE, Math.round(target)));
}

export function startPlaying(g: LocalGame): boolean {
  const named = g.players.filter(p => p.name.trim().length > 0);
  if (named.length < 2) return false;
  g.players = named.map(p => ({ name: p.name.trim(), score: 0 }));
  g.phase = 'playing';
  g.activeIdx = 0;
  g.turnPoints = 0;
  g.draftDice = [];
  g.diceCommittedThisTurn = 0;
  g.winnerIdx = null;
  g.hotDiceFlag = false;
  g.prevSnapshot = null;
  pushLog(g, `Spiel gestartet — Ziel ${g.targetScore} Punkte.`);
  return true;
}

export function addDraftDie(g: LocalGame, face: DieFace): boolean {
  if (g.phase !== 'playing') return false;
  if (g.draftDice.length + g.diceCommittedThisTurn >= DICE_COUNT) return false;
  g.draftDice.push(face);
  g.hotDiceFlag = false;
  return true;
}

export function removeDraftDie(g: LocalGame, idx: number): void {
  if (g.phase !== 'playing') return;
  g.draftDice.splice(idx, 1);
}

export function clearDraft(g: LocalGame): void {
  g.draftDice = [];
}

export function draftScore(g: LocalGame): { points: number; valid: boolean } {
  return scoreSelection(g.draftDice);
}

export function commitSelection(g: LocalGame): { ok: boolean; reason?: string } {
  if (g.phase !== 'playing') return { ok: false, reason: 'WRONG_PHASE' };
  if (g.draftDice.length === 0) return { ok: false, reason: 'EMPTY' };
  const sel = scoreSelection(g.draftDice);
  if (!sel.valid) return { ok: false, reason: 'INVALID' };

  g.prevSnapshot = takeSnapshot(g, 'Wertung');
  g.turnPoints += sel.points;
  g.diceCommittedThisTurn += g.draftDice.length;
  pushLog(g, `${activeName(g)}: Wertung +${sel.points} (Zug-Punkte ${g.turnPoints}).`);
  g.draftDice = [];

  if (g.diceCommittedThisTurn >= DICE_COUNT) {
    g.diceCommittedThisTurn = 0;
    g.hotDiceFlag = true;
    pushLog(g, `Hot Dice! ${activeName(g)} darf alle 6 Würfel neu werfen.`);
  } else {
    g.hotDiceFlag = false;
  }
  return { ok: true };
}

export function bankPoints(g: LocalGame): { ok: boolean; reason?: string } {
  if (g.phase !== 'playing') return { ok: false, reason: 'WRONG_PHASE' };
  let pendingFromDraft = 0;
  if (g.draftDice.length > 0) {
    const sel = scoreSelection(g.draftDice);
    if (!sel.valid) return { ok: false, reason: 'INVALID_DRAFT' };
    pendingFromDraft = sel.points;
  }
  const total = g.turnPoints + pendingFromDraft;
  if (total <= 0) return { ok: false, reason: 'NOTHING_TO_BANK' };

  g.prevSnapshot = takeSnapshot(g, 'Banken');
  const player = g.players[g.activeIdx];
  player.score += total;
  pushLog(g, `${player.name} bankt ${total} (Gesamt ${player.score}).`);
  g.draftDice = [];
  g.turnPoints = 0;
  g.diceCommittedThisTurn = 0;
  g.hotDiceFlag = false;

  if (player.score >= g.targetScore) {
    g.phase = 'finished';
    g.winnerIdx = g.activeIdx;
    pushLog(g, `${player.name} gewinnt!`);
    return { ok: true };
  }

  nextTurn(g);
  return { ok: true };
}

export function bust(g: LocalGame): void {
  if (g.phase !== 'playing') return;
  g.prevSnapshot = takeSnapshot(g, 'Bust');
  pushLog(g, `${activeName(g)} bustet — ${g.turnPoints} Punkte verloren.`);
  g.draftDice = [];
  g.turnPoints = 0;
  g.diceCommittedThisTurn = 0;
  g.hotDiceFlag = false;
  nextTurn(g);
}

function nextTurn(g: LocalGame): void {
  g.activeIdx = (g.activeIdx + 1) % g.players.length;
  g.draftDice = [];
  g.turnPoints = 0;
  g.diceCommittedThisTurn = 0;
  g.hotDiceFlag = false;
}

export function rematch(g: LocalGame): void {
  for (const p of g.players) p.score = 0;
  g.phase = 'playing';
  g.activeIdx = 0;
  g.turnPoints = 0;
  g.draftDice = [];
  g.diceCommittedThisTurn = 0;
  g.winnerIdx = null;
  g.hotDiceFlag = false;
  g.log = [];
  g.prevSnapshot = null;
  pushLog(g, `Neue Runde — Ziel ${g.targetScore} Punkte.`);
}

export function activeName(g: LocalGame): string {
  return g.players[g.activeIdx]?.name ?? '?';
}

export function diceLeftThisTurn(g: LocalGame): number {
  return DICE_COUNT - g.diceCommittedThisTurn - g.draftDice.length;
}
